import { accumulation } from "./accumulation.ts";
import { datesInclusive, parseIsoDate } from "./date.ts";
import type { Entry, IsoDate, MatrixDocument, Outcome } from "./model.ts";
import { firstRecordedDate, scoreDay, scoreTrail } from "./scoring.ts";

export interface ScoreImpactSnapshot {
  readonly score: number | null;
  readonly numerator: number;
  readonly denominator: number;
}

export interface HistoricalDayImpact {
  readonly date: IsoDate;
  readonly before: ScoreImpactSnapshot;
  readonly after: ScoreImpactSnapshot;
}

export interface HistoricalTrailImpact {
  readonly days: number;
  readonly before: number | null;
  readonly after: number | null;
}

export interface OutsideScheduleEntry {
  readonly operationId: string;
  readonly date: IsoDate;
  readonly outcome: Outcome;
}

export interface ManualByeRelevance {
  readonly operationId: string;
  readonly date: IsoDate;
  readonly outcome: "B";
}

export interface ManualByeImpact {
  readonly stillRelevant: readonly ManualByeRelevance[];
  readonly newlyRelevant: readonly ManualByeRelevance[];
  readonly redundant: readonly ManualByeRelevance[];
  readonly defaultRedundantAction: "keep";
  readonly removalRequiresConfirmation: true;
}

export interface HistoricalImpactOptions {
  readonly from?: IsoDate;
  readonly trailDays?: readonly number[];
  /** undefined derives the anchor from each document; null deliberately has no anchor. */
  readonly anchorBefore?: IsoDate | null;
  readonly anchorAfter?: IsoDate | null;
}

export interface HistoricalImpactPreview {
  readonly from: IsoDate | null;
  readonly through: IsoDate;
  readonly changedDays: readonly HistoricalDayImpact[];
  readonly today: { readonly before: number | null; readonly after: number | null };
  readonly trails: readonly HistoricalTrailImpact[];
  readonly accumulation: {
    readonly anchorBefore: IsoDate | null;
    readonly anchorAfter: IsoDate | null;
    readonly before: number;
    readonly after: number;
    readonly delta: number;
  };
  readonly newlyOutsideScheduleEntries: readonly OutsideScheduleEntry[];
  readonly newlyIncludedEntries: readonly OutsideScheduleEntry[];
  readonly manualByes: ManualByeImpact;
}

function earliestRelevantDate(documents: readonly MatrixDocument[]): IsoDate | null {
  const dates = documents.flatMap((document) => [
    ...document.categories.flatMap((category) => category.activePeriods.map((period) => period.from)),
    ...document.operations.flatMap((operation) => operation.activePeriods.map((period) => period.from)),
    ...document.entries.map((entry) => entry.date),
  ]);
  for (const date of dates) parseIsoDate(date);
  return dates.sort()[0] ?? null;
}

function snapshot(document: MatrixDocument, date: IsoDate, asOf: IsoDate): ScoreImpactSnapshot {
  const day = scoreDay(document, date, { asOf });
  return { score: day.score, numerator: day.numerator, denominator: day.denominator };
}

function sameSnapshot(left: ScoreImpactSnapshot, right: ScoreImpactSnapshot): boolean {
  return left.score === right.score && left.numerator === right.numerator && left.denominator === right.denominator;
}

function entryKey(entry: Pick<Entry, "operationId" | "date">): string {
  return `${entry.operationId}\u0000${entry.date}`;
}

function outsideScheduleEntries(document: MatrixDocument): OutsideScheduleEntry[] {
  return document.entries.flatMap((entry) => {
    const contribution = scoreDay(document, entry.date, { operationIds: [entry.operationId] }).contributions[0];
    return contribution?.recordedOutsideSchedule
      ? [{ operationId: entry.operationId, date: entry.date, outcome: entry.outcome }]
      : [];
  });
}

function withoutEntry(document: MatrixDocument, operationId: string, date: IsoDate): MatrixDocument {
  return {
    ...document,
    entries: document.entries.filter((entry) => entry.operationId !== operationId || entry.date !== date),
  };
}

function manualByeCouldBeRequired(document: MatrixDocument, operationId: string, date: IsoDate, asOf: IsoDate): boolean {
  const contribution = scoreDay(withoutEntry(document, operationId, date), date, {
    operationIds: [operationId],
    asOf,
  }).contributions[0];
  return contribution?.displaySource === "required-blank" || contribution?.displaySource === "unresolved-blank";
}

function changedScheduleOperations(beforeDocument: MatrixDocument, afterDocument: MatrixDocument): Set<string> {
  const beforeCategories = new Map(beforeDocument.categories.map((category) => [category.id, category]));
  const afterCategories = new Map(afterDocument.categories.map((category) => [category.id, category]));
  const beforeOperations = new Map(beforeDocument.operations.map((operation) => [operation.id, operation]));
  const changed = new Set<string>();
  for (const after of afterDocument.operations) {
    const before = beforeOperations.get(after.id);
    if (!before) continue;
    const beforeCategory = beforeCategories.get(before.categoryId);
    const afterCategory = afterCategories.get(after.categoryId);
    const beforeSchedule = JSON.stringify({ activePeriods: before.activePeriods, ruleVersions: before.ruleVersions, categoryActivePeriods: beforeCategory?.activePeriods ?? [] });
    const afterSchedule = JSON.stringify({ activePeriods: after.activePeriods, ruleVersions: after.ruleVersions, categoryActivePeriods: afterCategory?.activePeriods ?? [] });
    if (beforeSchedule !== afterSchedule) changed.add(after.id);
  }
  return changed;
}

function manualByeImpact(
  beforeDocument: MatrixDocument,
  afterDocument: MatrixDocument,
  from: IsoDate | null,
  through: IsoDate,
): ManualByeImpact {
  const changedOperations = changedScheduleOperations(beforeDocument, afterDocument);
  const beforeManualByes = new Set(beforeDocument.entries.filter((entry) => entry.outcome === "B").map(entryKey));
  const stillRelevant: ManualByeRelevance[] = [];
  const newlyRelevant: ManualByeRelevance[] = [];
  const redundant: ManualByeRelevance[] = [];
  for (const entry of afterDocument.entries) {
    if (
      entry.outcome !== "B" ||
      !beforeManualByes.has(entryKey(entry)) ||
      !changedOperations.has(entry.operationId) ||
      (from !== null && entry.date < from)
    ) continue;
    const item = { operationId: entry.operationId, date: entry.date, outcome: "B" as const };
    const beforeRelevant = manualByeCouldBeRequired(beforeDocument, entry.operationId, entry.date, through);
    const afterRelevant = manualByeCouldBeRequired(afterDocument, entry.operationId, entry.date, through);
    if (!afterRelevant) redundant.push(item);
    else if (beforeRelevant) stillRelevant.push(item);
    else newlyRelevant.push(item);
  }
  return {
    stillRelevant,
    newlyRelevant,
    redundant,
    defaultRedundantAction: "keep",
    removalRequiresConfirmation: true,
  };
}

/** Compare two complete owner documents using the same scoring engine used by
 * Today, trails, and accumulation. This is intentionally fingerprint-free: the
 * protected server workflow adds revisions and a canonical digest around this
 * deterministic payload. */
export function previewHistoricalImpact(
  beforeDocument: MatrixDocument,
  afterDocument: MatrixDocument,
  through: IsoDate,
  options: HistoricalImpactOptions = {},
): HistoricalImpactPreview {
  parseIsoDate(through);
  const from = options.from ?? earliestRelevantDate([beforeDocument, afterDocument]);
  if (from !== null) parseIsoDate(from);
  const changedDays: HistoricalDayImpact[] = [];
  if (from !== null && from <= through) {
    for (const date of datesInclusive(from, through)) {
      const before = snapshot(beforeDocument, date, through);
      const after = snapshot(afterDocument, date, through);
      if (!sameSnapshot(before, after)) changedDays.push({ date, before, after });
    }
  }

  const trailDays = options.trailDays ?? [7, 28];
  const trails = trailDays.map((days): HistoricalTrailImpact => {
    const definition = { id: `${days}d`, label: `${days} day`, days };
    return {
      days,
      before: scoreTrail(beforeDocument, definition, through).score,
      after: scoreTrail(afterDocument, definition, through).score,
    };
  });
  const anchorBefore = options.anchorBefore === undefined ? firstRecordedDate(beforeDocument, through) : options.anchorBefore;
  const anchorAfter = options.anchorAfter === undefined ? firstRecordedDate(afterDocument, through) : options.anchorAfter;
  const beforeAccumulation = accumulation(beforeDocument, through, anchorBefore).total;
  const afterAccumulation = accumulation(afterDocument, through, anchorAfter).total;

  const beforeOutside = outsideScheduleEntries(beforeDocument);
  const afterOutside = outsideScheduleEntries(afterDocument);
  const beforeOutsideKeys = new Set(beforeOutside.map(entryKey));
  const afterOutsideKeys = new Set(afterOutside.map(entryKey));
  const afterEntryKeys = new Set(afterDocument.entries.map(entryKey));

  return {
    from,
    through,
    changedDays,
    today: {
      before: scoreDay(beforeDocument, through, { asOf: through }).score,
      after: scoreDay(afterDocument, through, { asOf: through }).score,
    },
    trails,
    accumulation: {
      anchorBefore,
      anchorAfter,
      before: beforeAccumulation,
      after: afterAccumulation,
      delta: afterAccumulation - beforeAccumulation,
    },
    newlyOutsideScheduleEntries: afterOutside.filter((entry) => !beforeOutsideKeys.has(entryKey(entry))),
    newlyIncludedEntries: beforeOutside.filter((entry) =>
      !afterOutsideKeys.has(entryKey(entry)) && afterEntryKeys.has(entryKey(entry))
    ),
    manualByes: manualByeImpact(beforeDocument, afterDocument, from, through),
  };
}

